/**
 * CampaignHistory component for viewing past notification campaigns
 * Stat card filters like Notification Audit Log
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, AlertCircle, Search, Filter, X } from 'lucide-react';
import {
  NotificationCampaign,
  CampaignStatus,
  getCampaigns,
  getChannelLabel,
  NotificationChannel,
} from '../../utils/notificationsBroadcastApi';

type StatusFilter = 'all' | 'completed' | 'failed';
type ChannelFilter = NotificationChannel | 'all';

interface CampaignStats {
  total: number;
  completed: number;
  failed: number;
  totalSent: number;
}

export const CampaignHistory: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<NotificationCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await getCampaigns();
      setCampaigns(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Calculate stats
  const stats: CampaignStats = useMemo(() => {
    return {
      total: campaigns.length,
      completed: campaigns.filter(c => c.status === 'completed').length,
      failed: campaigns.filter(c => c.status === 'failed').length,
      totalSent: campaigns.reduce((sum, c) => sum + c.sent_count, 0),
    };
  }, [campaigns]);

  // Handle stat card click
  const handleStatCardClick = (filter: StatusFilter) => {
    setStatusFilter(filter);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: CampaignStatus) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            Completed
          </span>
        );
      case 'sending':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
            Sending
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
            Failed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            {status}
          </span>
        );
    }
  };

  const handleRowClick = (campaign: NotificationCampaign) => {
    navigate(`/dashboard/broadcast?tab=history&campaignId=${campaign.id}`);
  };

  // Filtered campaigns
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      // Status filter
      if (statusFilter === 'completed' && campaign.status !== 'completed') {
        return false;
      }
      if (statusFilter === 'failed' && campaign.status !== 'failed') {
        return false;
      }
      
      // Channel filter
      if (channelFilter !== 'all' && campaign.channel !== channelFilter) {
        return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const name = (campaign.name || campaign.list_name || '').toLowerCase();
        const sentBy = (campaign.sent_by_user_name || '').toLowerCase();
        if (!name.includes(query) && !sentBy.includes(query)) {
          return false;
        }
      }
      
      return true;
    });
  }, [campaigns, statusFilter, channelFilter, searchQuery]);

  const clearFilters = () => {
    setStatusFilter('all');
    setChannelFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = statusFilter !== 'all' || channelFilter !== 'all';

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="h-20 bg-gray-200 dark:bg-dark-bg-tertiary rounded-lg" />
            <div className="h-20 bg-gray-200 dark:bg-dark-bg-tertiary rounded-lg" />
            <div className="h-20 bg-gray-200 dark:bg-dark-bg-tertiary rounded-lg" />
            <div className="h-20 bg-gray-200 dark:bg-dark-bg-tertiary rounded-lg" />
          </div>
          <div className="h-64 bg-gray-200 dark:bg-dark-bg-tertiary rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
        {/* Stats Cards - Clickable to filter */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <button
            onClick={() => handleStatCardClick('all')}
            className={`text-left rounded-lg p-4 transition-all ${
              statusFilter === 'all'
                ? 'bg-gray-100 dark:bg-dark-bg-tertiary ring-2 ring-gray-400 dark:ring-gray-500'
                : 'bg-gray-50 dark:bg-dark-bg-tertiary hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
            }`}
          >
            <div className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
              {stats.total}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Total Campaigns</div>
          </button>
          <button
            onClick={() => handleStatCardClick('completed')}
            className={`text-left rounded-lg p-4 transition-all ${
              statusFilter === 'completed'
                ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-500'
                : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
            }`}
          >
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.completed}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Completed</div>
          </button>
          <button
            onClick={() => handleStatCardClick('failed')}
            className={`text-left rounded-lg p-4 transition-all ${
              statusFilter === 'failed'
                ? 'bg-red-100 dark:bg-red-900/30 ring-2 ring-red-500'
                : 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
            }`}
          >
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {stats.failed}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Failed</div>
          </button>
          <div className="text-left rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.totalSent}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Total Sent</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by campaign name or sender..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              hasActiveFilters
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {(statusFilter !== 'all' ? 1 : 0) + (channelFilter !== 'all' ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary">Additional Filters</span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-dark-text-muted dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                  Clear all
                </button>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs text-gray-500 dark:text-dark-text-muted mb-1">Channel</label>
                <select
                  value={channelFilter}
                  onChange={(e) => setChannelFilter(e.target.value as ChannelFilter)}
                  className="text-sm border border-gray-300 dark:border-dark-border rounded-md px-3 py-1.5 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Channels</option>
                  <option value="sms">SMS</option>
                  <option value="email_html">HTML Email</option>
                  <option value="email_plain">Plain Email</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {campaigns.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-dark-bg-tertiary flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">
              No campaigns yet
            </h3>
            <p className="text-gray-500 dark:text-dark-text-muted max-w-sm">
              When you send broadcasts, they'll appear here.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Campaign
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Sent By
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Channel
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Sent
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Clicks
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Failed
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border bg-white dark:bg-dark-bg-secondary">
              {filteredCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500 dark:text-dark-text-muted">
                    <p className="text-sm">No campaigns match your filters.</p>
                    <button
                      onClick={clearFilters}
                      className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Clear filters
                    </button>
                  </td>
                </tr>
              ) : null}
              {filteredCampaigns.map((campaign) => (
                <tr
                  key={campaign.id}
                  onClick={() => handleRowClick(campaign)}
                  className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    {getStatusBadge(campaign.status)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900 dark:text-dark-text-primary">
                      {campaign.name || campaign.list_name || 'All Customers'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-dark-text-secondary">
                    {campaign.sent_by_user_name || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-dark-text-secondary">
                    {getChannelLabel(campaign.channel)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600 dark:text-green-400">
                    {campaign.sent_count}
                  </td>
                  <td className="px-4 py-3">
                    {campaign.click_count > 0 ? (
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        {campaign.click_rate}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-dark-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {campaign.failed_count > 0 ? (
                      <span className="text-sm font-medium text-red-600 dark:text-red-400">
                        {campaign.failed_count}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-dark-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-dark-text-muted whitespace-nowrap">
                    {formatDate(campaign.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CampaignHistory;
